import React, { ChangeEvent, useState, Fragment } from 'react';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { Trans, useTranslation } from 'react-i18next';
import { styled, useTheme } from '@mui/material/styles';
import StarTwoToneIcon from '@mui/icons-material/StarTwoTone';
import useMediaQuery from '@mui/material/useMediaQuery';
import slice from 'lodash/slice';
import { Picture } from '../Picture';
import { useGoogleTagManager } from '../../src/hooks/useGoogleTagManager';
import { USER_FLOW_ID } from '../../src/helpers/constants';
import { NoticeMessage } from './NoticeMessage';
import { ratings, rows } from './data';
import { StyledSelect } from './StyledSelect';
import { StyledRating } from './StyledRating';

const Success = styled('span')(() => ({
  color: '#00997D',
}));

const H6 = styled(Typography)({
  fontWeight: 600,
  fontSize: '18px',
  lineHeight: '34px',
  color: '#6C7889',
}) as typeof Typography;

const UI18Regular = styled(Typography)(() => ({
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: '24px',
}));

const UI20Regular = styled(Typography)(() => ({
  fontWeight: 600,
  fontSize: '18px',
  lineHeight: '34px',
}));

const StyledTableRow = styled(TableRow)({
  '& td, & th': { borderBottomColor: '#E4E9F1' },
});

const ValuesTableRow = styled(StyledTableRow)<{ background?: number }>(({ theme, background }) => ({
  [theme.breakpoints.up('sm')]: {
    height: '99px',
  },
  backgroundColor: background ? '#F8F8FD' : 'inherit',
}));

const XsSubcategoryTableRow = styled(StyledTableRow)({
  height: '22px',
});

const HeaderRow = styled(StyledTableRow)(() => ({
  '& td, & th': { borderBottom: 0 },
}));

const CategoryTableRow = styled(StyledTableRow)(() => ({
  height: '65px',
}));

const ValueContainer = styled('div')(({ theme }) => ({
  '& > :not(style)': {
    marginRight: theme.spacing(),
  },
}));

export interface BanksComparisonTableProps {
  defaultBanks?: string[];
  className?: string;
}

const ALL_BANKS = ['comdirect', 'dab', 'ebase', 'fondsdepotbank', 'ffb', 'morgenfund'];

export const BanksComparisonTable = ({
  defaultBanks = ['comdirect', 'dab', 'ebase'],
  className,
}: BanksComparisonTableProps) => {
  const { t } = useTranslation();
  const [banks, setBanks] = useState(defaultBanks);
  const theme = useTheme();
  const isXsDown = useMediaQuery(theme.breakpoints.down('sm'));

  const handleBankChange = (index: number, newBank: string) => {
    setBanks((current) => {
      const newBanks = [...current];
      newBanks[index] = newBank;
      return newBanks;
    });
  };

  const sendToGoogleTagManager = useGoogleTagManager(USER_FLOW_ID);

  const banksResponsive: string[] = isXsDown ? slice(banks, 0, banks.length - 1) : banks;

  return (
    <TableContainer className={className} data-sectionid="banks-comparison-table">
      <Table aria-label="simple table">
        <TableHead>
          <HeaderRow>
            {!isXsDown && <TableCell></TableCell>}
            {banksResponsive.map((slug, index) => (
              <TableCell align="center" key={`selector-${slug}-${index}`}>
                <StyledSelect
                  native
                  variant="outlined"
                  value={slug}
                  onClick={() =>
                    sendToGoogleTagManager({
                      event: 'button-click',
                      buttonId: `selector-${slug}-${index}`,
                      contextId: 'banks-comparison-table',
                    })
                  }
                  onChange={(event) =>
                    handleBankChange(
                      index,
                      (event as ChangeEvent<HTMLInputElement>).currentTarget?.value
                    )
                  }
                >
                  {ALL_BANKS.map((bankSlug) => (
                    <option value={bankSlug} key={`select-option-${bankSlug}-${index}`}>
                      {t(`banksComparisonTable.${bankSlug}.name`)}
                    </option>
                  ))}
                </StyledSelect>
              </TableCell>
            ))}
          </HeaderRow>
          <HeaderRow>
            {!isXsDown && <TableCell></TableCell>}
            {banksResponsive.map((slug, index) => (
              <TableCell align="center" key={`icon-${slug}-${index}`}>
                <Picture baseImagePath={`/images/banks/${slug}`} width="130" title={slug} />
              </TableCell>
            ))}
          </HeaderRow>
        </TableHead>
        <TableBody>
          {rows.map((item) => (
            <Fragment key={item.key}>
              {item.type === 'row' && (
                <>
                  {isXsDown && (
                    <XsSubcategoryTableRow key={`subcategory-${item.key}`}>
                      <TableCell component="th" colSpan={2}>
                        <H6>{t(`banksComparisonTable.${item.key}`)}</H6>
                      </TableCell>
                    </XsSubcategoryTableRow>
                  )}
                  <ValuesTableRow key={item.key} background={item.background || isXsDown ? 1 : 0}>
                    {!isXsDown && (
                      <TableCell component="th" scope="row">
                        <H6>{t(`banksComparisonTable.${item.key}`)}</H6>
                      </TableCell>
                    )}
                    {banksResponsive.map((slug, index) => (
                      <TableCell align="center" key={`${item.key}-${slug}-${index}`}>
                        <ValueContainer>
                          <H6 component="span">
                            <Trans i18nKey={`banksComparisonTable.${slug}.${item.key}`}>
                              Value <Success>success</Success>
                            </Trans>
                          </H6>
                          <NoticeMessage slug={slug} rowKey={item.key} />
                        </ValueContainer>
                      </TableCell>
                    ))}
                  </ValuesTableRow>
                </>
              )}
              {item.type === 'category' && (
                <CategoryTableRow key={item.key}>
                  <TableCell component="th" colSpan={isXsDown ? 2 : 4}>
                    <UI18Regular>{t(`banksComparisonTable.${item.key}`)}</UI18Regular>
                  </TableCell>
                </CategoryTableRow>
              )}
            </Fragment>
          ))}

          {isXsDown && (
            <XsSubcategoryTableRow>
              <TableCell component="th" colSpan={2}>
                <UI20Regular>{t(`banksComparisonTable.rating`)}</UI20Regular>
              </TableCell>
            </XsSubcategoryTableRow>
          )}

          <ValuesTableRow>
            {!isXsDown && (
              <TableCell component="th" scope="row">
                <UI20Regular>{t(`banksComparisonTable.rating`)}</UI20Regular>
              </TableCell>
            )}

            {banksResponsive.map((slug, index) => (
              <TableCell align="center" key={`rating-${slug}-${index}`}>
                <StyledRating
                  name="read-only"
                  value={ratings[slug]}
                  readOnly
                  size={isXsDown ? 'small' : 'medium'}
                  icon={<StarTwoToneIcon fontSize="inherit" />}
                />
              </TableCell>
            ))}
          </ValuesTableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};
